import React, { useState, useEffect } from "react";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
// import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getPreferenceLabel } from "../../constants";
import { getPreferences, savePreferences } from "../../api";
import {
    Box,
    Tooltip,
    Typography,
    CircularProgress,
    IconButton,
    Button,
    Alert,
    Popover,
} from "@mui/material";
import "./APITesting.css";
import { toast } from "react-toastify";

const ClientPreferences = ({ onBack, onNext }) => {
    const [preferences, setPreferences] = useState({});
    const [loading, setLoading] = useState(true);
    const [changedPreferences, setChangedPreferences] = useState([]);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [popoverData, setPopoverData] = React.useState(null);

    const handlePopoverClick = (event, preference) => {
        if (preference.example || preference.combinedExample) {
            setAnchorEl(event.currentTarget);
            setPopoverData(preference);
        }
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setPopoverData(null);
    };

    useEffect(() => {
        const fetchPreferences = async () => {
            try {
                const clientId = localStorage.getItem("clientInfo")
                    ? JSON.parse(localStorage.getItem("clientInfo"))
                          .productionClientId
                    : null;

                if (!clientId) {
                    console.error("Client ID not found");
                    return;
                }

                const response = await getPreferences(clientId, "client");

                if (response.status === 200 && response.data) {
                    // Group preferences by category
                    const groupedPreferences = response.data.reduce(
                        (acc, pref) => {
                            if (!acc[pref.category]) {
                                acc[pref.category] = [];
                            }
                            const exist = acc[pref.category].find(
                                (p) => p.fieldName === pref.fieldName,
                            );
                            if (!exist) acc[pref.category].push(pref);
                            return acc;
                        },
                        {},
                    );

                    setPreferences(groupedPreferences);
                }
            } catch (error) {
                console.error("Error fetching preferences:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPreferences();
    }, []);

    const handlePreferenceChange = (category, fieldName, checked, id) => {
        setPreferences((prev) => ({
            ...prev,
            [category]: prev[category].map((pref) =>
                pref.fieldName === fieldName
                    ? { ...pref, fieldValue: checked }
                    : pref,
            ),
        }));

        // Track changed preferences
        setChangedPreferences((prev) => {
            const existingIndex = prev.findIndex(
                (item) => item.fieldName === fieldName,
            );
            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = {
                    id,
                    fieldName,
                    fieldValue: checked,
                };
                return updated;
            }
            return [
                ...prev,
                {
                    id,
                    fieldName,
                    fieldValue: checked,
                },
            ];
        });
    };

    const validateUnselectedPreference = () => {
        let noSelections = [];
        Object.values(preferences).forEach((categoryPreferences) => {
            noSelections = [
                ...noSelections,
                ...categoryPreferences.filter(
                    (preference) => preference.fieldValue === null,
                ),
            ];
        });
        return noSelections;
    };

    const handleNextClick = async () => {
        setSubmitLoading(true);
        const noSelections = validateUnselectedPreference();
        if (noSelections.length) {
            setSubmitLoading(false);
            toast.error(
                `Please select Yes/No for ${getPreferenceLabel(
                    noSelections[0].fieldName,
                )}`,
            );
            return;
        }
        if (!changedPreferences.length) {
            onNext();
            setSubmitLoading(false);
            return;
        }
        const { productionClientId: clientId } = JSON.parse(
            localStorage.getItem("clientInfo") || "{}",
        );
        try {
            const response = await savePreferences(
                clientId,
                changedPreferences,
            );
            if (response.status === 200) {
                console.log("Preferences updated successfully:", response.data);
                onNext();
            } else {
                console.error("Failed to update preferences:", response);
            }
        } catch (error) {
            console.error("Error updating preferences:", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const preferenceItem = (preference, onChange) => (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 0",
            }}
        >
            <div>
                <div
                    style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}
                >
                    {getPreferenceLabel(preference.fieldName)}
                </div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>
                    {preference.description}
                </div>
                {!preference.isHtSupported && (
                    <div
                        style={{
                            color: "#DFA43A",
                            fontStyle: "italic",
                            fontSize: 12,
                            marginTop: 4,
                        }}
                    >
                        Not yet available
                    </div>
                )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                {preference?.example?.length &&
                preference?.combinedExample?.length ? (
                    <IconButton
                        className={`tooltip-icon ${
                            !preference.isHtSupported ? "not-supported" : ""
                        }`}
                        onClick={(e) => handlePopoverClick(e, preference)}
                    >
                        <LightbulbOutlinedIcon />
                    </IconButton>
                ) : preference?.example?.length ? (
                    <Tooltip title={`Example: ${preference.example}` || ""}>
                        <IconButton
                            className={`tooltip-icon ${
                                !preference.isHtSupported ? "not-supported" : ""
                            }`}
                        >
                            <LightbulbOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <div
                    style={{
                        width: 1,
                        height: 36,
                        background: "#E5E7EB",
                        margin: "0 16px 0 0",
                    }}
                />
                <Button
                    variant={
                        preference.fieldValue === true
                            ? "contained"
                            : "outlined"
                    }
                    className={`choice-button ${
                        preference.fieldValue === true ? "selected" : ""
                    }`}
                    onClick={() => onChange(true)}
                >
                    Yes
                </Button>
                <Button
                    variant={
                        preference.fieldValue === false
                            ? "contained"
                            : "outlined"
                    }
                    className={`choice-button ${
                        preference.fieldValue === false ? "selected" : ""
                    }`}
                    onClick={() => onChange(false)}
                >
                    No
                </Button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading preferences...</Typography>
            </Box>
        );
    }

    const open = Boolean(anchorEl);
    const popoverId = open ? "simple-popover" : undefined;

    return (
        <div className="client-preferences">
            {/* Title and subtitle at the top */}
            <Typography
                sx={{
                    fontWeight: 700,
                    color: "#374151",
                    fontSize: 20,
                    mb: 0.5,
                }}
            >
                Client preferences
            </Typography>
            <Typography
                sx={{ color: "#6B7280", fontSize: "14px !important", mb: 2 }}
            >
                Please indicate the configurations you support by selecting{" "}
                <b>Yes</b> or <b>No</b> for each item below.
            </Typography>
            {/* Info section at the top */}
            <Alert
                severity={"info"}
                sx={{
                    "& .MuiAlert-icon": {
                        padding: "4px",
                        marginTop: "4px",
                        backgroundColor: "#dfa43a",
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                    },
                    "& .MuiAlert-icon > .MuiSvgIcon-root": {
                        border: "0",
                        color: "#fff !important",
                        width: "0.75em",
                        height: "0.75em",
                    },
                    "& .MuiAlert-message": {
                        fontSize: "14px",
                        alignContent: "center",
                        color: "#663c00",
                    },
                }}
            >
                Some features are not yet available, but we're actively working
                to launch them.{" "}
                <b>
                    By selecting them, you will get access as soon as they're
                    ready.
                </b>
            </Alert>
            <Popover
                id={popoverId}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                {popoverData?.example && (
                    <div className="example">
                        <div className="example-title">Example:</div>
                        <div className="example-text">
                            <pre>{popoverData.example}</pre>
                        </div>
                    </div>
                )}
                {popoverData?.combinedExample && (
                    <div className="combined-example">
                        <strong>Combined example:</strong>
                        <pre>{popoverData.combinedExample}</pre>
                    </div>
                )}
            </Popover>
            {Object.entries(preferences).map(
                ([category, categoryPreferences]) => (
                    <div className="preferences-section">
                        <h3
                            key={category}
                            className={
                                category === "Products API"
                                    ? "preferences-section-title-1"
                                    : "preferences-section-title"
                            }
                        >
                            {category}
                        </h3>
                        {category === "Products API" && (
                            <Typography
                                key={`${category}-desc`}
                                className="preferences-section-description"
                                variant="h6"
                            >
                                Choose the features your system can support.
                            </Typography>
                        )}
                        <div className="preferences-grid">
                            {categoryPreferences.map((preference) =>
                                preferenceItem(preference, (checked) =>
                                    handlePreferenceChange(
                                        preference.category,
                                        preference.fieldName,
                                        checked,
                                        preference.id,
                                    ),
                                ),
                            )}
                        </div>
                    </div>
                ),
            )}

            <div className="button-group">
                <button className="back-link" onClick={onBack}>
                    {"<"} Back to Credentials
                </button>
                <button
                    className="next-button"
                    onClick={handleNextClick}
                    disabled={submitLoading}
                >
                    {submitLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        "Next step >"
                    )}
                </button>
            </div>
        </div>
    );
};

export default ClientPreferences;
